import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";

const COUNTRY_IMAGES = {
  TR: "https://images.pexels.com/photos/32634768/pexels-photo-32634768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000",
  CN: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?auto=format&fit=crop&w=1000&h=800&q=85",
  US: "https://images.pexels.com/photos/18388935/pexels-photo-18388935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000",
  AE: "https://images.pexels.com/photos/36909910/pexels-photo-36909910.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000",
  FR: "https://images.pexels.com/photos/21621081/pexels-photo-21621081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000",
  GB: "https://images.pexels.com/photos/32753691/pexels-photo-32753691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000",
};

export default function ShopByCountry() {
  const { t, isRTL } = useLanguage();

  return (
    <section
      id="countries"
      data-testid="countries-section"
      className="relative bg-[#FAFAFA] py-24 text-[#111] md:py-32 dark:bg-[#080808] dark:text-white"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 max-w-3xl md:mb-16">
          <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
            — {t.countries.eyebrow}
          </span>
          <h2 className="mb-5 font-display text-4xl font-bold leading-[1.08] sm:text-5xl md:text-6xl">
            {t.countries.title}
          </h2>
          <p className="text-base leading-relaxed text-[#5B6473] md:text-lg dark:text-[#A3A3A3]">
            {t.countries.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {t.countries.items.map((c, i) => (
            <motion.div
              key={c.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <Link
                to={`/country/${c.code.toLowerCase()}`}
                id={`country-${c.code}`}
                onClick={() => {
                  trackEvent("shipping_country_select", {
                    routeName: "home_country_grid",
                    country: { code: c.code, name: c.name },
                    metadata: { source: "shop_by_country" },
                  });
                }}
                className="group relative isolate block h-[360px] cursor-pointer overflow-hidden rounded-[28px] border border-black/10 shadow-[0_26px_60px_rgba(0,0,0,0.22)] scroll-mt-24 dark:border-white/10"
                data-testid={`country-card-${c.code}`}
              >
              {/* Background image */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.04]"
                style={{ backgroundImage: `url(${COUNTRY_IMAGES[c.code]})` }}
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/72 to-black/22 transition-colors group-hover:from-[#050505]" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/22 via-transparent to-transparent" />
              {/* Hover orange tint */}
              <div className="absolute inset-0 bg-[#F37021] opacity-0 group-hover:opacity-10 transition-opacity" />

              {/* Flag top-left */}
              <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-white/30 bg-black/45 px-3 py-1.5 shadow-[0_12px_28px_rgba(0,0,0,0.28)] backdrop-blur-md rtl:left-auto rtl:right-5">
                <img
                  src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                  alt={c.name}
                  className="w-5 h-[14px] object-cover rounded-[2px]"
                />
                <span className="text-[11px] font-semibold tracking-wider uppercase text-white">{c.code}</span>
              </div>

              {/* Content */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                <div className="rounded-[24px] border border-white/16 bg-black/52 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.32)] backdrop-blur-sm transition-colors group-hover:bg-black/62">
                <h3 className="mb-2 font-display text-3xl font-bold leading-none text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:text-4xl">
                  {c.name}
                </h3>
                <p className="mb-4 max-w-[260px] text-sm font-semibold leading-6 text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)]">{c.tagline}</p>

                <div className="inline-flex translate-y-2 items-center gap-2 text-sm font-semibold text-[#F37021] opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                  <span>{t.countries.shopCta} {c.name}</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </div>
                </div>
              </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
